import { useEffect } from 'react';

import { useChannelSettingsLoadFailures, useLoadingState } from '../../hooks';
import { PROJECT_DATA_LOADED_IDLE_STATE } from '../../hooks/loading-states';
import { type ReportingApiCredentialRow, type RuleSet } from '../../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
  useProStudioStateMatchesOneOf,
} from '../../xstate/app.xstate';
import { nextChannelSettingsLoad } from '../../xstate/channel-settings-load-status';

// How a channel's Live Agent Platform / Reporting API / Auto-masking bindings
// are read. Shared by the Channel Settings form and the Design stage's channel
// panel, so the two resolve a channel's bindings the same way.

type ChannelEnv = 'live' | 'sandbox';

const boundTo = (
  pypes: { pypeId: string; env: string }[],
  pypeId: string,
  env: string
) =>
  pypes.some(
    (pype) =>
      pype.pypeId === pypeId && pype.env.toLowerCase() === env.toLowerCase()
  );

/** The rule set assigned to this pype+env ('' = none, i.e. the default). */
export const findAssignedRuleSetId = (
  ruleSets: RuleSet[] | null | undefined,
  pypeId: string | undefined,
  env: string | undefined
): string => {
  if (!pypeId || !env) return '';
  return (
    (ruleSets ?? []).find((set) => boundTo(set.pypes, pypeId, env))?.id ?? ''
  );
};

// The Live Agent Platform reads are the shared ones (PSTUDIO-4344), so the
// Design stage hides AgentPro exactly like Channel Settings does.
export {
  findBoundConnectionIds,
  resolveConnectionBinding,
  toPlatformConnectionOptions,
} from '../../utils';

/**
 * Loads what a channel's template sections show — rule sets, THIS channel's
 * Reporting API credentials and Live Agent Platform connections — the same way
 * the Channel Settings form does (PSTUDIO-4253 / PSTUDIO-3897): one load at a
 * time while the machine is idle, credentials scoped to the channel's own pype
 * and env, and a failed step counted as settled so the block stops promising a
 * spinner. Used by the Design stage's channel panel.
 */
export const useChannelTemplateData = ({
  enabled,
  pypeId,
  env,
  isSaving = false,
}: {
  enabled: boolean;
  pypeId: string | undefined;
  env: string | undefined;
  isSaving?: boolean;
}) => {
  const {
    pypeRuleGroups,
    channelApiCredentials,
    pypeConnections,
    ruleGroupsFailureCount,
    apiCredentialsFailureCount,
    liveAgentFailureCount,
  } = useProStudioCtxSelector((ctx) => ({
    pypeRuleGroups: ctx.pypeRuleGroups,
    channelApiCredentials: ctx.channelApiCredentials,
    pypeConnections: ctx.pypeConnections,
    ruleGroupsFailureCount: ctx.channelSettingsLoadFailures?.ruleGroups,
    apiCredentialsFailureCount: ctx.channelSettingsLoadFailures?.apiCredentials,
    liveAgentFailureCount: ctx.channelSettingsLoadFailures?.liveAgentPlatforms,
  }));
  const isPageLoading = useLoadingState();
  const isMachineIdle = useProStudioStateMatchesOneOf([
    PROJECT_DATA_LOADED_IDLE_STATE,
  ]);

  const ruleGroupsLoaded = pypeRuleGroups != null;
  const channelHasPype = Boolean(pypeId && env);
  const apiCredentialsLoaded =
    !channelHasPype ||
    (channelApiCredentials != null &&
      channelApiCredentials.pypeId === pypeId &&
      channelApiCredentials.env === env);
  const liveAgentLoaded = pypeConnections != null;

  const {
    ruleGroupsFailed,
    apiCredentialsFailed,
    liveAgentFailed,
    recordDispatch,
  } = useChannelSettingsLoadFailures({
    ruleGroupsLoaded,
    apiCredentialsLoaded,
    liveAgentLoaded,
    ruleGroupsFailureCount,
    apiCredentialsFailureCount,
    liveAgentFailureCount,
  });

  useEffect(() => {
    if (!enabled || isPageLoading || !isMachineIdle) {
      return;
    }
    const next = nextChannelSettingsLoad({
      ruleGroupsLoaded,
      ruleGroupsFailed,
      apiCredentialsLoaded,
      apiCredentialsFailed,
      liveAgentLoaded,
      liveAgentFailed,
      isSaving,
    });
    if (next === 'ruleGroups') {
      recordDispatch('ruleGroups', ruleGroupsFailureCount);
      sendProStudioEvent({ type: 'loadPypeRuleGroups' });
    } else if (next === 'apiCredentials') {
      recordDispatch('apiCredentials', apiCredentialsFailureCount);
      sendProStudioEvent({
        type: 'loadChannelApiCredentials',
        payload: { pypeId: pypeId as string, env: env as ChannelEnv },
      });
    } else if (next === 'liveAgentPlatforms') {
      recordDispatch('liveAgentPlatforms', liveAgentFailureCount);
      sendProStudioEvent({ type: 'loadLiveAgentPlatforms' });
    }
  }, [
    enabled,
    isPageLoading,
    isMachineIdle,
    isSaving,
    pypeId,
    env,
    ruleGroupsLoaded,
    apiCredentialsLoaded,
    liveAgentLoaded,
    ruleGroupsFailed,
    apiCredentialsFailed,
    liveAgentFailed,
    ruleGroupsFailureCount,
    apiCredentialsFailureCount,
    liveAgentFailureCount,
    recordDispatch,
  ]);

  const credentials: ReportingApiCredentialRow[] =
    channelApiCredentials &&
    channelApiCredentials.pypeId === pypeId &&
    channelApiCredentials.env === env
      ? channelApiCredentials.rows
      : [];

  return {
    pypeRuleGroups,
    pypeConnections,
    credentials,
    ruleGroupsLoaded,
    ruleGroupsFailed,
    liveAgentLoaded,
    liveAgentFailed,
  };
};
